import React from 'react';
import { Components, Theme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
// eslint-disable-next-line no-restricted-imports
import { chipClasses } from '@mui/material/Chip';

import { ICON_SIZES } from '../Icon/const';

export const CHIP_HEIGHT = {
  SMALL: 24,
  MEDIUM: 32,
  LARGE: 40
};

export default {
  defaultProps: {
    variant: 'default',
    color: 'secondary',
    corner: 'round',
    deleteIcon: <ClearIcon data-test-id='chip-remove-btn' />,
    'data-test-id': 'chip',
  },
  styleOverrides: {
    sizeSmall: ({ theme }) => ({
      height: CHIP_HEIGHT.SMALL,
      minWidth: CHIP_HEIGHT.SMALL,
      borderRadius: 8,
      ...theme.typography.overline,
      '&.bold': {
        ...theme.typography.overlineBold,
      }
    }),
    sizeMedium: ({ theme }) => ({
      height: CHIP_HEIGHT.MEDIUM,
      minWidth: CHIP_HEIGHT.MEDIUM,
      borderRadius: 10,
      ...theme.typography.body2,
      '&.bold': {
        ...theme.typography.body2Bold,
      },
      '&.MuiChip-stacked': {
        ...theme.typography.overlineBold,
        '& .MuiChip-title': {
          marginRight: 0,
          marginBottom: `-${theme.spacing(0.5)}`,
          ...theme.typography.caption,
        },
      }
    }),
    sizeLarge: ({ theme }) => ({
      height: CHIP_HEIGHT.LARGE,
      minWidth: CHIP_HEIGHT.LARGE,
      borderRadius: 12,
      ...theme.typography.body1,
      '&.bold': {
        ...theme.typography.body1Bold,
      },
      '&.MuiChip-stacked': {
        ...theme.typography.body2Bold,
        '& .MuiChip-title': {
          marginRight: 0,
          marginBottom: `-${theme.spacing(0.25)}`,
          ...theme.typography.overline,
        },
      }

    }),
    icon: ({ theme }) => {
      return {
        margin: 0,
        paddingLeft: theme.spacing(0.5),
        fontSize: ICON_SIZES.X_SMALL,
      };
    },
    [`&.${chipClasses.sizeMedium} > .${chipClasses.icon}`]: ({ theme }: { theme: Theme }) => {
      return {
        paddingLeft: theme.spacing(0.75),
        fontSize: ICON_SIZES.SMALL,
        '&.MuiChip-endIcon': {
          paddingLeft: theme.spacing(0.75),
        }
      };
    },
    iconLarge: ({ theme }) => {
      return {
        paddingLeft: theme.spacing(1),
        fontSize: ICON_SIZES.MEDIUM,
        '&.MuiChip-endIcon': {
          paddingLeft: theme.spacing(0.5),
        }
      };
    },
    deleteIcon: ({ theme }) => {
      return {
        fontSize: ICON_SIZES.X_SMALL,
        opacity: 0.5,
        paddingRight: theme.spacing(0.5),
        margin: 0,
        '&:hover': {
          opacity: 1,
        },
      };
    },
    [`&.${chipClasses.sizeMedium} > .${chipClasses.deleteIcon}`]: ({ theme }: { theme: Theme }) => {
      return {
        fontSize: ICON_SIZES.SMALL,
        paddingRight: theme.spacing(0.75),
      };
    },
    deleteIconLarge: ({ theme }) => {
      return {
        fontSize: ICON_SIZES.MEDIUM,
        paddingRight: theme.spacing(1),
      };
    },
    label: ({ theme }) => {
      return {
        '&.withStartIcon': {
          paddingLeft: theme.spacing(0.5),
        },
        '&.withEndIcon, &.withDeleteIcon': {
          paddingRight: theme.spacing(0.5),
        },
        '&:empty': {
          paddingRight: 0
        },
      };
    },
    [`&.${chipClasses.sizeSmall} > .${chipClasses.label}`]: ({ theme }: { theme: Theme }) => {
      return {
        padding: theme.spacing(0, 1),
      };
    },
    [`&.${chipClasses.sizeMedium} > .${chipClasses.label}`]: ({ theme }: { theme: Theme }) => {
      return {
        padding: theme.spacing(0, 1.25),
      };
    },
    labelLarge: ({ theme }) => {
      return {
        padding: theme.spacing(0, 1.5),
      };
    },
    root: ({ theme }) => ({
      position: 'relative',
      color: theme.palette.neutral.onSurface,
      '& .MuiTouchRipple-root': {
        display: 'none',
      },
      '&.MuiChip-corner-pill': {
        borderRadius: 360,
      },
      '& .MuiChip-title': {
        display: 'inline',
        marginRight: theme.spacing(0.5),
        opacity: 0.75,
      },
      '&.MuiChip-stacked': {
        ...theme.typography.body2Bold,
        '& .MuiChip-title': {
          display: 'block',
          opacity: 1,
          ...theme.typography.overline,
        },
      },
    }),
    default: ({ theme, ownerState }) => {
      const colorName = ownerState.color!;

      const stateLayer = theme.createStateLayer(
        'transparent',
      );
      return {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.neutral.outlineVariant,
        backgroundColor: 'transparent',
        color: theme.palette.neutral.onSurface,
        '& .MuiChip-deleteIcon': {
          color: theme.palette.neutral.onSurfaceVariant,
          '&:hover': {
            color: theme.palette.neutral.onSurfaceVariant,
          }
        },
        '& .MuiChip-icon, svg': {
          color: theme.palette[colorName].outline,
        },
        '&.MuiChip-clickable': {
          ...stateLayer,
          '&:focus': {
            backgroundColor: 'transparent',
          }
        }
      };
    },
    filled: ({ theme, ownerState }) => {
      const colorName = ownerState.color!;

      const stateLayer = theme.createStateLayer(
        theme.palette[colorName].main
      );
      return {
        backgroundColor: theme.palette[colorName].main,
        color: theme.palette[colorName].onMain,
        '& .MuiChip-deleteIcon': {
          color: theme.palette[colorName].onMain,
          '&:hover': {
            color: theme.palette[colorName].onMain,
          }
        },
        '& .MuiChip-icon, svg': {
          color: theme.palette[colorName].onMain,
        },
        '&.Mui-disabled': stateLayer['&.Mui-disabled'],
        '&.MuiChip-clickable': {
          ...stateLayer,
          '&:focus': {
            backgroundColor: theme.palette[colorName].main,
          }
        }
      };
    },

    outlined: ({ theme, ownerState }) => {
      const colorName = ownerState.color!;

      const stateLayer = theme.createStateLayer(
        'transparent',
        theme.palette[colorName].main
      );
      return {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette[colorName].main,
        backgroundColor: 'transparent',
        color: theme.palette.neutral.onSurface,
        '& .MuiChip-title': {
          color: theme.palette.neutral.onSurface,
        },
        '& .MuiChip-deleteIcon': {
          color: theme.palette.neutral.onSurfaceVariant,
          '&:hover': {
            color: theme.palette.neutral.onSurfaceVariant,
          }
        },
        '& .MuiChip-icon, svg': {
          color: theme.palette[colorName].outline,
        },
        '&.MuiChip-clickable': {
          ...stateLayer,
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      };
    },
    tonal: ({ theme, ownerState }) => {
      const colorName = ownerState.color!;

      const stateLayer = theme.createStateLayer(
        theme.palette[colorName].container
      );
      return {
        backgroundColor: theme.palette[colorName].container,
        color: theme.palette[colorName].onContainer,
        '& .MuiChip-deleteIcon': {
          color: theme.palette[colorName].onContainer,
          '&:hover': {
            color: theme.palette[colorName].onContainer,
          }
        },
        '& .MuiChip-icon, svg': {
          color: theme.palette[colorName].outline,
        },
        '&.MuiChip-clickable': {
          ...stateLayer,
          '&:focus': {
            backgroundColor: theme.palette[colorName].container,
          }
        }
      };
    },
    tonalOutlined: ({ theme, ownerState }) => {
      const colorName = ownerState.color!;

      const stateLayer = theme.createStateLayer(
        theme.palette[colorName].container,
        theme.palette[colorName].main
      );
      return {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette[colorName].main,
        backgroundColor: theme.palette[colorName].container,
        color: theme.palette[colorName].onContainer,
        '& .MuiChip-deleteIcon': {
          color: theme.palette[colorName].onContainer,
          '&:hover': {
            color: theme.palette[colorName].onContainer,
          }
        },
        '& .MuiChip-icon, svg': {
          color: theme.palette[colorName].outline,
        },
        '&.MuiChip-clickable': {
          ...stateLayer,
          '&:focus': {
            backgroundColor: theme.palette[colorName].container,
          }
        }
      };
    }
  }
} as Components<Theme>['MuiChip'];
